import FreeBusyCalendarItem from './FreeBusyCalendarItem';
import FreeBusyRow, { emptyFreeBusyRow } from './schema/FreeBusyRow';
import FreeBusyType from './schema/FreeBusyType';
import getMostSevereFreeBusy from './utils/getMostSevereFreeBusy';
import debounce from 'lodash-es/debounce';
import { ObservableMap } from 'mobx';
import { isConnectedAccount } from 'owa-accounts-store';
import { getAvailabilityFunction, PageQueryResponse } from 'owa-availability-service';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getUserMailboxInfo } from 'owa-client-ids';
import { MILLISECONDS_IN_DAY, MILLISECONDS_IN_MINUTE } from 'owa-date-constants';
import { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import { MruCache } from 'owa-mru-cache';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { trace } from 'owa-trace';
import { mutatorAction } from 'satcheljs';
import {
    utcDate,
    OwaDate,
    owaDate,
    getTimestamp,
    isEqual,
    addDays,
    addMilliseconds,
    isBefore,
    isAfter,
    startOfDay,
} from 'owa-datetime';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';

const SLOT_SIZE_IN_MINUTES = 15;

export default class FreeBusyCacheManager {
    private readonly epoch: OwaDate;
    private readonly epochTime: number;
    private readonly pageSizeInMilliseconds: number;
    private readonly slotsPerPage: number;
    private readonly cacheSize: number;

    private store: ObservableMap<string, ObservableMap<string, FreeBusyRow>>;
    private attendeeDictionary: { [id: string]: MruCache<FreeBusyRow> };
    private attendeeFiltersDictionary: { [id: string]: CalendarEvent };
    private attendeeWorkingHoursDictionary: ObservableMap<string, WorkingHoursType>;

    // freebusy query queue parameters
    private readonly maxBatchSize: number = 10;
    private pagesRequestedTime: OwaDate = null;
    private requestedAddressesForSamePage: string[] = [];

    constructor(pageSizeInDays: number, cacheSize: number) {
        this.epoch = utcDate(1970, 0, 1);
        this.epochTime = getTimestamp(this.epoch);
        this.cacheSize = cacheSize;
        this.pageSizeInMilliseconds = pageSizeInDays * MILLISECONDS_IN_DAY;
        this.slotsPerPage =
            this.pageSizeInMilliseconds / (MILLISECONDS_IN_MINUTE * SLOT_SIZE_IN_MINUTES);
    }

    public initialize(store: ObservableMap<string, ObservableMap<string, FreeBusyRow>>) {
        this.store = store;
        this.attendeeDictionary = {};
        this.attendeeFiltersDictionary = {};
        this.attendeeWorkingHoursDictionary = new ObservableMap<string, WorkingHoursType>();
    }

    /**
     * Gets the WorkingHours for an attendee
     * @param attendee The attendee for which working hours are requested
     */
    public getAttendeeWorkingHours(attendee: AttendeeType): WorkingHoursType {
        const address = attendee.Mailbox?.EmailAddress;
        const defaultWorkingHours = getUserConfiguration().UserOptions.WorkingHours;
        if (!address) {
            return defaultWorkingHours;
        }
        if (!this.attendeeWorkingHoursDictionary.has(address)) {
            updateAttendeeWorkingHours(
                this.attendeeWorkingHoursDictionary,
                address,
                defaultWorkingHours
            );
        }
        return this.attendeeWorkingHoursDictionary.get(address);
    }

    public getAttendeeDetailedEvents(attendee: string, start: OwaDate, end: OwaDate) {
        const tz = start.tz;
        const startPageIndex = this.getPageIndex(start);
        const endPageIndex = this.getPageIndex(end);
        const events: CalendarEvent[] = [];

        for (let i = startPageIndex; i <= endPageIndex; i++) {
            const page = this.tryGetFreeBusy(attendee, i);
            // NOTE: Start/End in free-busy item comes in UTC and without offsets.
            // NOTE: It looks like Charm is not being respected;
            //       we always get None in the response.
            const pageEvents = ((page.Items as FreeBusyCalendarItem[]) || [])
                .filter(e => e.FreeBusyType != 'Free')
                .map(
                    item =>
                        ({
                            ...item,
                            Subject: item.Subject,
                            End: owaDate(tz, utcDate(item.End)),
                            Start: owaDate(tz, utcDate(item.Start)),
                            ItemId: { Id: item.ItemId.Id },
                            IsFreeBusyOnly: !('DetailedMerged' === page.FreeBusyViewType),
                        } as CalendarEvent)
                );

            events.push(...pageEvents);
        }

        return events;
    }

    /**
     * Gets an array of free busy information for an attendee between the start and end time
     * @param attendee The SMTP address for which free busy is requested
     * @param start The start time for which to retrieve free busy
     * @param end The end time for which to retrieve free busy
     * @param filter An optional item to filter out of the free busy results
     */
    public getDetailedFreeBusy(
        attendee: string,
        start: OwaDate,
        end: OwaDate,
        filter?: CalendarEvent
    ): FreeBusyType[] {
        const startPageIndex = this.getPageIndex(start);
        const endPageIndex = this.getPageIndex(end);
        const startRecordIndex = Math.floor(this.getRecordIndex(start));
        let endRecordIndex = Math.ceil(this.getRecordIndex(end));

        let mergedFreeBusy: FreeBusyType[] = [];

        // Special handling for <15 minute events.
        if (startPageIndex == endPageIndex && startRecordIndex == endRecordIndex) {
            endRecordIndex++;
        }

        for (let i = startPageIndex; i <= endPageIndex; i++) {
            let page = this.tryGetFreeBusy(attendee, i, filter).mergedFreeBusy;

            if (i == endPageIndex) {
                page = page.slice(0, endRecordIndex);
            }
            if (i == startPageIndex) {
                page = page.slice(startRecordIndex);
            }
            mergedFreeBusy.push(...page);
        }

        return mergedFreeBusy;
    }

    /**
     * Gets the most severe free busy status between start and end time
     * @param attendee: The SMTP address for which Free Busy was requested
     * @param start: The start time of the event
     * @param end: The end time of the event
     * @param isAllDayEvent: true if the event spans the entire day
     * @param filter: An optional CalendarEvent to filter out of the results
     */
    public getAggregatedFreeBusy(
        attendee: string,
        start: OwaDate,
        end: OwaDate,
        isAllDayEvent: boolean,
        filter?: CalendarEvent
    ): FreeBusyType {
        start = isAllDayEvent ? startOfDay(start) : start;
        end = isAllDayEvent ? startOfDay(addDays(end, 1)) : end;
        let mergedFreeBusy = this.getDetailedFreeBusy(attendee, start, end, filter);

        return mergedFreeBusy.reduce(
            (previous, current) => getMostSevereFreeBusy(previous, current),
            FreeBusyType.Loading
        );
    }

    /**
     * Adds new email addresses to the cache
     * @param emailAddress {string} The SMTP address for the new attendee
     */
    private addEmailAddress(emailAddress: string) {
        if (!this.store.has(emailAddress)) {
            mutatorAction('Initialize Attendee free busy store', (attendee: string) => {
                this.store.set(attendee, new ObservableMap<string, FreeBusyRow>());
            })(emailAddress);
            this.attendeeDictionary[emailAddress] = new MruCache<FreeBusyRow>(this.cacheSize);
            this.attendeeDictionary[emailAddress].initialize(this.store.get(emailAddress));
        }
    }

    /**
     * Gets the page number at which the specified time should be found.
     * @param time: The time to perform the lookup against
     */
    private getPageIndex(time: OwaDate) {
        let timeSinceEpoch = getTimestamp(time) - this.epochTime;
        return Math.floor(timeSinceEpoch / this.pageSizeInMilliseconds);
    }

    /**
     * Gets the row number in a page in which the specified time should be found.
     * @param time: The time to perform the lookup against
     */
    private getRecordIndex(time: OwaDate) {
        let timeSinceEpoch = getTimestamp(time) - this.epochTime;
        return (
            (timeSinceEpoch % this.pageSizeInMilliseconds) /
            (MILLISECONDS_IN_MINUTE * SLOT_SIZE_IN_MINUTES)
        );
    }

    /**
     * Checks to see if free busy information is available for the given attendee and time.
     * If no free busy is available, initializes the whole page to 5 FreeBusyType.NoData and
     * requests fresh information.
     * @param attendee: The attendee email address
     * @param pageIndex: The page index to request
     */
    private tryGetFreeBusy(
        attendee: string,
        pageIndex: number,
        filter?: CalendarEvent
    ): FreeBusyRow {
        let dictionaryKey = pageIndex.toString();

        if (!this.attendeeDictionary[attendee]) {
            this.addEmailAddress(attendee);
        }

        // Once we cached the results of an attendee, further request with different filter won't
        // apply in the same session as we read from cache. Actually querying f/b of one attendee
        // with different filters shouldn't happen in practice. For new events, there is no filter;
        // for existing meeting, the original event should be always applied as the filter for attendees
        // in the original meeting.
        let page = this.attendeeDictionary[attendee].get(dictionaryKey);
        if (!page) {
            // Initialize a full page with Loading
            this.attendeeDictionary[attendee].add(dictionaryKey, {
                FreeBusyViewType: 'FreeBusyMerged',
                mergedFreeBusy: emptyFreeBusyRow(this.slotsPerPage, FreeBusyType.Loading),
            });

            // Request new data from server
            this.requestFreeBusyPage(attendee, pageIndex, filter);

            page = this.attendeeDictionary[attendee].get(dictionaryKey);
        }

        return page;
    }

    /**
     * Returns true if the provided event has a full or partial conflict with the provided daterange
     * @param event The CalendarEvent to check for
     * @param start The start of the daterange
     * @param end The end of the daterange
     */
    private isConflict(event: OwaDateRange, start: OwaDate, end: OwaDate) {
        return isAfter(event.end, start) && isBefore(event.start, end);
    }

    /**
     * Attempts to remove the event being edited from the server's free busy results
     * @param freeBusy The freeBusy object, as received from the server
     * @param rangeStart The start date for the provided free busy object
     * @param rangeEnd The end date for the provided free busy object
     * @param attendeeResponse The response for the event for the attendee whos FB is being patched
     */
    private tryFilterOutCalendarEvent(
        freeBusy: FreeBusyRow,
        rangeStart: OwaDate,
        rangeEnd: OwaDate,
        filter: CalendarEvent,
        attendeeResponse: ResponseTypeType
    ) {
        // Only patch the organizer and attendees who have accepted the meeting.
        if (attendeeResponse == 'Accept' || attendeeResponse == 'Organizer') {
            // Compute which portions of the Free Busy need to be patched.
            const start = filter.Start;
            const end = filter.End;
            if (filter && this.isConflict({ start, end }, rangeStart, rangeEnd)) {
                let filterStart = isBefore(start, rangeStart) ? rangeStart : start;
                let filterEnd = isAfter(end, rangeEnd) ? rangeEnd : end;
                let startRecordIndex = this.getRecordIndex(filterStart);
                let endRecordIndex = this.getRecordIndex(filterEnd);

                /*
                / Hypothesis: Organizing or accepting a meeting request is an implicit commitment
                / to be available for the duration of the event. Therefore, marking such time as
                / free is arguably valid.
                /
                / Drawback: This code will also wipe out other events occurring in the same time
                / period. More nuanced filtering would be possible by either:
                /
                / a) Performing the filtering in the server, where complete data is available for
                / every attendee,
                /
                / b) Obtaining an ItemId for each of the items of all the attendees (possibly an
                / information leak) or,
                /
                / c) As a last resort, best effort filtering might also be possible. This would
                / attempt to match based on either an ItemId or times and FreeBusyType depending on
                / access level. After the matching and filtering, the merged free busy can be
                / recomputed from the remaining items.
                */
                for (let i = startRecordIndex; i < endRecordIndex; i++) {
                    freeBusy.mergedFreeBusy[i] = FreeBusyType.Free;
                }
            }
        }
        return freeBusy;
    }

    /**
     * Requests a fresh free busy page from server
     * @param attendee: The email address for which free busy should be requested
     * @param pageIndex: The page index to request
     * @param attendeeDictionary: The dictionary of MRU caches
     * @param filter: An optional CalendarEvent to filter out of the results
     */
    private requestFreeBusyPage(attendee: string, pageIndex: number, filter?: CalendarEvent) {
        // The filter for each attendee f/b request needs to be cached. We batch the
        // requests for attendees' availability. For example, we request the availability
        // of p0, p1 and p2 with filter0, filter1 and filter2 respectively. The three requests
        // could be batched into one and we get the response for three attendees. In the continuation function,
        // we need to make sure the correct filter is applied to the response of each attendee.
        this.attendeeFiltersDictionary[attendee] = filter;

        this.getAvailabilityForSingleAddress(attendee, pageIndex);
    }

    // Request f/b of single address. If it is organizer, the request will be out immediately;
    // Otherwise, the address is put in a queue. Once the query page index has changed, or the
    // queue reaches the size limit, or an amount of time has elapsed since the first address
    // was added to the queue, we send one request to query the status of all addresses in the queue.
    private getAvailabilityForSingleAddress(address: string, pageIndex: number) {
        const rangeStart = addMilliseconds(this.epoch, this.pageSizeInMilliseconds * pageIndex);
        const rangeEnd = addMilliseconds(rangeStart, this.pageSizeInMilliseconds);
        if (
            address === getUserConfiguration().SessionSettings.UserEmailAddress ||
            isConnectedAccount(address)
        ) {
            const mailboxInfo = getUserMailboxInfo(address);
            // Never batch the organizer. As their availability is much less likely to time out.
            getAvailabilityFunction(
                rangeStart,
                rangeEnd,
                [address],
                this.processPageQueryResponsesInSamePage(pageIndex, rangeStart, rangeEnd),
                true /*retryOnFail*/,
                mailboxInfo,
                SLOT_SIZE_IN_MINUTES
            );

            return;
        }

        // If there was a page jump, resolve availability for pending attendees and start a new batch.
        if (!isEqual(this.pagesRequestedTime, rangeStart)) {
            this.debouncedGetAvailability.flush();
            this.pagesRequestedTime = rangeStart;
        }

        this.requestedAddressesForSamePage.push(address);
        this.debouncedGetAvailability(
            rangeStart,
            rangeEnd,
            pageIndex,
            this.requestedAddressesForSamePage.slice()
        );

        if (this.requestedAddressesForSamePage.length >= this.maxBatchSize) {
            this.debouncedGetAvailability.flush();
        }
    }

    private debouncedGetAvailability = debounce(
        (start: OwaDate, end: OwaDate, pageIndex: number, attendees: string[]) => {
            this.requestedAddressesForSamePage = [];

            getAvailabilityFunction(
                start,
                end,
                attendees,
                this.processPageQueryResponsesInSamePage(pageIndex, start, end),
                true, //retryOnFail
                null, //mailbox info
                SLOT_SIZE_IN_MINUTES
            );
        },
        500
    );

    private processPageQueryResponsesInSamePage(
        pageIndex: number,
        rangeStart: OwaDate,
        rangeEnd: OwaDate
    ) {
        return (pageQueryResponse: PageQueryResponse) => {
            if (!pageQueryResponse) {
                return;
            }

            Object.keys(pageQueryResponse).forEach(address => {
                // It is possible that a new session has started when we get the response.
                // Skip if we don't query the f/b of this address; otherwise, we can still
                // cache the f/b for the particular attendee.
                if (!this.attendeeDictionary[address]) {
                    return;
                }

                const userAvailability = pageQueryResponse[address];
                const { MergedFreeBusy } = userAvailability;

                // Add working hours of attendee to the dictionary if not already added.
                if (this.attendeeWorkingHoursDictionary && userAvailability.WorkingHours) {
                    updateAttendeeWorkingHours(
                        this.attendeeWorkingHoursDictionary,
                        address,
                        userAvailability.WorkingHours
                    );
                }

                let freeBusyRow: FreeBusyRow = {
                    ...userAvailability,
                    mergedFreeBusy:
                        userAvailability.FreeBusyViewType == 'None'
                            ? emptyFreeBusyRow(this.slotsPerPage, FreeBusyType.NoData)
                            : MergedFreeBusy
                            ? MergedFreeBusy.split('').map(value => parseInt(value))
                            : [],
                };

                const cachedFilter = this.attendeeFiltersDictionary[address];
                if (cachedFilter) {
                    let attendeeResponses = {};

                    [
                        ...cachedFilter.RequiredAttendees,
                        ...cachedFilter.OptionalAttendees,
                        ...cachedFilter.Resources,
                    ].forEach(
                        eventAttendee =>
                            (attendeeResponses[eventAttendee.Mailbox.EmailAddress] =
                                eventAttendee.ResponseType)
                    );

                    if (cachedFilter.Organizer) {
                        attendeeResponses[cachedFilter.Organizer.Mailbox.EmailAddress] =
                            'Organizer';
                    }

                    const attendeeResponse = attendeeResponses[address];
                    if (!attendeeResponse) {
                        trace.warn(`Unknown response for attendee ${address}`);
                    }

                    freeBusyRow = this.tryFilterOutCalendarEvent(
                        freeBusyRow,
                        rangeStart,
                        rangeEnd,
                        cachedFilter,
                        attendeeResponse
                    );
                }

                this.attendeeDictionary[address].add(pageIndex.toString(), freeBusyRow);
            });
        };
    }
}

const updateAttendeeWorkingHours = mutatorAction(
    'updateAttendeeWorkingHours',
    (
        attendeeWorkingHoursDictionary: ObservableMap<string, WorkingHoursType>,
        address: string,
        workingHours: WorkingHoursType
    ) => {
        attendeeWorkingHoursDictionary.set(address, workingHours);
    }
);
